import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/useStore';
import { filterSlice } from '../../store/filterSlice';
import { MarketPriceFilterOptionValueSorterEnum } from '../../constants/filter/marketPriceFilter.constant';
import { priceSliderValueMapping } from '../../constants/filter/slider/filterSliderConstant';

export const useUpdateMarketPriceFilterStates = () => {
  const dispatch = useAppDispatch();
  const marketPriceFilterStates = useAppSelector((state) => state.filter.marketPriceFilter);
  const filterActions = filterSlice.actions;

  const [searchParams] = useSearchParams();

  const sortSearchParam = useMemo<string>(() => {
    if (searchParams.has('sort')) {
      return searchParams.get('sort')!;
    }
    return '';
  }, [searchParams]);

  const brandSearchParam = useMemo<string>(() => {
    if (searchParams.has('brand')) {
      return searchParams.get('brand')!;
    }
    return '';
  }, [searchParams]);

  const minEngineOutputPsSearchParam = useMemo<string>(() => {
    if (searchParams.has('minEngineOutputPs')) {
      return searchParams.get('minEngineOutputPs')!;
    }
    return '';
  }, [searchParams]);

  const maxEngineOutputPsSearchParam = useMemo<string>(() => {
    if (searchParams.has('maxEngineOutputPs')) {
      return searchParams.get('maxEngineOutputPs')!;
    }
    return '';
  }, [searchParams]);

  const minMedianPriceSearchParam = useMemo<string>(() => {
    if (searchParams.has('minMedianPrice')) {
      return searchParams.get('minMedianPrice')!;
    }
    return '';
  }, [searchParams]);

  const maxMedianPriceSearchParam = useMemo<string>(() => {
    if (searchParams.has('maxMedianPrice')) {
      return searchParams.get('maxMedianPrice')!;
    }
    return '';
  }, [searchParams]);

  // 실제 가격을 슬라이더 값으로 변환하는 함수
  const convertPriceToSliderValue = (price: number): number | undefined => {
    const entry = Object.entries(priceSliderValueMapping).find(([_, mappedPrice]) => mappedPrice === price);
    return entry ? Number(entry[0]) : undefined;
  };

  useEffect(() => {
    // search param 체크가 아직 안되었고, 필터 옵션에 brands가 모두 추가되었을 때 수행
    if (!marketPriceFilterStates.isSearchParamChecked && marketPriceFilterStates.isFilterOptionsAdded.brands) {
      // 1. 먼저 모든 필터를 리셋
      dispatch(filterActions.resetAllFilters({ filterType: 'marketPriceFilter' }));

      // 2. searchParams가 있을 때만 필터 업데이트
      if (searchParams) {
        // 정렬 필터 업데이트
        if (sortSearchParam) {
          Object.values(MarketPriceFilterOptionValueSorterEnum).forEach((sortOptionValues) => {
            if (sortSearchParam.includes(sortOptionValues as string)) {
              const targetFilter = marketPriceFilterStates.globalFilters.find((filter) => filter.key === 'sorter');
              const targetOption = targetFilter?.options.find((option) => option.value === sortOptionValues);

              if (targetFilter && targetOption) {
                dispatch(
                  filterActions.changeFilterOptionIsSelected({
                    filterType: 'marketPriceFilter',
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        // 브랜드 필터 업데이트
        if (brandSearchParam) {
          brandSearchParam.split(',').forEach((brandOptionValue) => {
            const targetFilter = marketPriceFilterStates.globalFilters.find((filter) => filter.key === 'filter_brand');
            const targetOption = targetFilter?.options.find((option) => option.value === brandOptionValue);

            if (targetFilter && targetOption) {
              dispatch(
                filterActions.changeFilterOptionIsSelected({
                  filterType: 'marketPriceFilter',
                  targetFilter,
                  targetOption,
                }),
              );
            }
          });
        }

        // 마력 필터 업데이트
        if (minEngineOutputPsSearchParam && maxEngineOutputPsSearchParam) {
          const targetFilter = marketPriceFilterStates.globalFilters.find(
            (filter) => filter.key === 'engineOutputInPs',
          );

          if (targetFilter) {
            dispatch(
              filterActions.changeFilterOptionValue({
                filterType: 'marketPriceFilter',
                targetFilter,
                optionValues: [Number(minEngineOutputPsSearchParam), Number(maxEngineOutputPsSearchParam)],
              }),
            );
          }
        }

        // 가격 필터 업데이트
        if (minMedianPriceSearchParam && maxMedianPriceSearchParam) {
          const targetFilter = marketPriceFilterStates.globalFilters.find((filter) => filter.key === 'medianPrice');

          if (targetFilter) {
            const minPrice = Number(minMedianPriceSearchParam);
            const maxPrice = Number(maxMedianPriceSearchParam);

            const minSliderValue = convertPriceToSliderValue(minPrice);
            const maxSliderValue = convertPriceToSliderValue(maxPrice);

            if (minSliderValue !== undefined && maxSliderValue !== undefined) {
              dispatch(
                filterActions.changeFilterOptionValue({
                  filterType: 'marketPriceFilter',
                  targetFilter,
                  optionValues: [minSliderValue, maxSliderValue],
                }),
              );
            }
          }
        }
      }

      // 3. 마지막으로 isSearchParamChecked를 true로 설정
      dispatch(filterActions.changeIsSearchParamChecked({ filterType: 'marketPriceFilter', value: true }));
    }
  }, [
    dispatch,
    filterActions,
    marketPriceFilterStates.globalFilters,
    marketPriceFilterStates.isSearchParamChecked,
    marketPriceFilterStates.isFilterOptionsAdded.brands,
    searchParams,
    brandSearchParam,
    sortSearchParam,
    minEngineOutputPsSearchParam,
    maxEngineOutputPsSearchParam,
    minMedianPriceSearchParam,
    maxMedianPriceSearchParam,
  ]);
};
